/**
 * 認証サービス（実API実装）
 *
 * バックエンドAPI呼び出し:
 * - POST /api/auth/login - ログイン認証
 * - POST /api/auth/logout - ログアウト
 * - GET /api/auth/me - 現在のユーザー情報取得
 * - POST /api/auth/password-reset/request - パスワードリセットメール送信
 * - POST /api/auth/password-reset/confirm - パスワードリセット実行
 * - POST /api/auth/password/change - パスワード変更
 * - PUT /api/auth/profile - プロフィール更新
 * - GET /api/auth/invite/:token - 招待トークン検証
 * - POST /api/auth/invite/accept - 招待受諾・アカウント作成
 */
import apiClient, { extractErrorMessage } from '../../lib/apiClient';
import type {
  User,
  LoginCredentials,
  AuthTokens,
  InviteVerifyResponse,
  CreateAccountRequest,
  UserRole,
} from '../../types';

// ===== バックエンドレスポンス型（snake_case） =====

interface BackendUserResponse {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'reviewer' | 'user';
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface BackendLoginResponse {
  user: BackendUserResponse;
  access_token: string;
  refresh_token?: string;
}

interface BackendInviteTokenInfo {
  valid: boolean;
  email?: string;
  role?: 'admin' | 'reviewer' | 'user';
  message?: string;
}

// ===== 変換関数（snake_case -> camelCase） =====

/**
 * バックエンドのユーザーレスポンスをフロントエンドの型に変換
 */
function convertUserResponse(backendUser: BackendUserResponse): User {
  return {
    id: backendUser.id,
    email: backendUser.email,
    name: backendUser.name,
    role: backendUser.role,
    status: backendUser.status,
    createdAt: backendUser.created_at,
    updatedAt: backendUser.updated_at,
  };
}

/**
 * バックエンドのログインレスポンスをフロントエンドの型に変換
 */
function convertLoginResponse(response: BackendLoginResponse): { user: User; tokens: AuthTokens } {
  return {
    user: convertUserResponse(response.user),
    tokens: {
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
    },
  };
}

/**
 * バックエンドの招待トークン情報をフロントエンドの型に変換
 */
function convertInviteTokenInfo(info: BackendInviteTokenInfo): InviteVerifyResponse {
  return {
    valid: info.valid,
    email: info.email,
    role: info.role as UserRole | undefined,
    message: info.message,
  };
}

// ===== リクエスト型 =====

interface UpdateProfileRequest {
  name?: string;
  email?: string;
}

interface PasswordChangeRequest {
  currentPassword: string;
  newPassword: string;
}

// ===== 認証サービス =====

export const authService = {
  /**
   * ログイン
   * POST /api/auth/login
   */
  async login(credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens }> {
    try {
      const response = await apiClient.post<BackendLoginResponse>('/api/auth/login', {
        email: credentials.email,
        password: credentials.password,
        remember_me: credentials.rememberMe ?? false,
      });
      return convertLoginResponse(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * ログアウト
   * POST /api/auth/logout
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 現在のユーザー情報取得
   * GET /api/auth/me
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get<BackendUserResponse>('/api/auth/me');
      return convertUserResponse(response.data);
    } catch (error) {
      // 認証エラーの場合はnullを返す
      return null;
    }
  },

  /**
   * パスワードリセットリクエスト
   * POST /api/auth/password-reset/request
   */
  async requestPasswordReset(email: string): Promise<void> {
    try {
      await apiClient.post('/api/auth/password-reset/request', { email });
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * パスワードリセット確認
   * POST /api/auth/password-reset/confirm
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await apiClient.post('/api/auth/password-reset/confirm', {
        token,
        new_password: newPassword,
      });
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * パスワード変更
   * POST /api/auth/password/change
   */
  async changePassword(request: PasswordChangeRequest): Promise<void> {
    try {
      await apiClient.post('/api/auth/password/change', {
        current_password: request.currentPassword,
        new_password: request.newPassword,
      });
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * プロフィール更新
   * PUT /api/auth/profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    try {
      const response = await apiClient.put<BackendUserResponse>('/api/auth/profile', {
        name: data.name,
        email: data.email,
      });
      return convertUserResponse(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  /**
   * 招待トークン検証
   * GET /api/auth/invite/:token
   */
  async verifyInviteToken(token: string): Promise<InviteVerifyResponse> {
    try {
      const response = await apiClient.get<BackendInviteTokenInfo>(`/api/auth/invite/${token}`);
      return convertInviteTokenInfo(response.data);
    } catch (error) {
      return {
        valid: false,
        message: extractErrorMessage(error),
      };
    }
  },

  /**
   * 招待受諾・アカウント作成
   * POST /api/auth/invite/accept
   */
  async createAccount(request: CreateAccountRequest): Promise<{ user: User; tokens: AuthTokens }> {
    try {
      const response = await apiClient.post<BackendLoginResponse>('/api/auth/invite/accept', {
        token: request.token,
        name: request.name,
        password: request.password,
      });
      return convertLoginResponse(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },
};

export default authService;
